/**
 * Appointment slot generation and availability.
 *
 * `generateSlots` is a faithful port of the legacy implementation in
 * doctor_dashboard.js (line ~5969) — including its break-overlap rule and the
 * "drop any slot that would run past the end time" behaviour — but rewritten in
 * integer minutes instead of throwaway `Date` objects, so it no longer depends
 * on the host's date parsing.
 *
 * The availability helpers replace logic that the legacy code re-derived inline
 * in half a dozen places (patient calendar, doctor calendar, manual booking
 * modal, move-booking modal), each with slightly different rules.
 */

import { timeToMinutes, minutesToTime, dayKeyOf } from './dates';

/**
 * Build the slot grid for one working day.
 *
 * @param {string} start     "HH:MM" inclusive start of the working window.
 * @param {string} end       "HH:MM" exclusive end of the working window.
 * @param {number} duration  Slot length in minutes.
 * @param {Array<{start: string, end: string}>} [breaks]
 * @returns {string[]} Slot start times as "HH:MM", ascending.
 */
export function generateSlots(start, end, duration, breaks = []) {
  const startMin = timeToMinutes(start);
  const endMin = timeToMinutes(end);
  const step = Number(duration);

  if (Number.isNaN(startMin) || Number.isNaN(endMin)) return [];
  if (!Number.isFinite(step) || step <= 0) return [];
  if (endMin <= startMin) return [];

  const windows = (Array.isArray(breaks) ? breaks : [])
    .map((b) => ({ start: timeToMinutes(b?.start), end: timeToMinutes(b?.end) }))
    .filter((b) => !Number.isNaN(b.start) && !Number.isNaN(b.end) && b.end > b.start);

  const slots = [];
  for (let current = startMin; current + step <= endMin; current += step) {
    const slotEnd = current + step;
    // A slot is dropped when it overlaps a break at all: (startA < endB) && (endA > startB)
    const collides = windows.some((b) => current < b.end && slotEnd > b.start);
    if (!collides) slots.push(minutesToTime(current));
  }
  return slots;
}

/**
 * Resolve the slot list for a clinic on a given date.
 *
 * Clinics store slots two ways in the live data: an explicit `slots` array
 * (older records, and the default created at registration) or a
 * startTime/endTime/duration/breaks window (the clinic editor writes this).
 * The window wins when it is complete, because the editor is what doctors use.
 *
 * @returns {string[]} Empty when the clinic does not work that weekday.
 */
export function clinicSlotsForDate(clinic, dateKey) {
  if (!clinic) return [];

  const workingDays = Array.isArray(clinic.workingDays) ? clinic.workingDays : [];
  if (dateKey && workingDays.length > 0) {
    const day = dayKeyOf(dateKey);
    const worksToday = workingDays.some((d) => String(d).toLowerCase() === day);
    if (!worksToday) return [];
  }

  const hasWindow =
    clinic.startTime && clinic.endTime && Number(clinic.duration) > 0;

  if (hasWindow) {
    return generateSlots(
      clinic.startTime,
      clinic.endTime,
      Number(clinic.duration),
      clinic.breaks || [],
    );
  }

  return Array.isArray(clinic.slots) ? [...clinic.slots] : [];
}

/** Bookings that still occupy a slot — anything not cancelled. */
export const OCCUPYING_STATUSES = ['pending', 'confirmed', 'completed', 'in_progress'];

export const occupiesSlot = (booking) =>
  Boolean(booking) && String(booking.status || 'pending') !== 'cancelled';

/**
 * How many live bookings sit on one clinic/date/time triple.
 * Mirrors the backend's own count in POST /api/bookings/create, so the UI and
 * the server agree on when a slot is full.
 */
export function countBookingsInSlot(bookings, { clinicId, dateKey, time }) {
  return (bookings || []).filter(
    (b) =>
      String(b.clinicId) === String(clinicId) &&
      b.date === dateKey &&
      b.time === time &&
      occupiesSlot(b),
  ).length;
}

/** True when the slot still has room under the clinic's `maxBookingsPerSlot`. */
export function isSlotAvailable(bookings, clinic, dateKey, time) {
  const capacity = Number(clinic?.maxBookingsPerSlot) || 1;
  return countBookingsInSlot(bookings, { clinicId: clinic?.id, dateKey, time }) < capacity;
}

/**
 * Decorate a day's slots with their booking state, ready for rendering.
 *
 * `extraSlots` are the evening times from a DoctorExtraHour block for this
 * clinic and date. They are appended and flagged `isExtra`, because a patient
 * who sees the "ساعات إضافية" badge on a day has to be able to book one of
 * those times — the badge is otherwise a promise the booking screen breaks.
 *
 * @returns {Array<{time, taken, capacity, available, isPast, isExtra}>} ascending
 */
export function buildSlotAvailability(
  clinic,
  dateKey,
  bookings,
  now = new Date(),
  extraSlots = [],
) {
  const capacity = Number(clinic?.maxBookingsPerSlot) || 1;
  const regular = clinicSlotsForDate(clinic, dateKey);

  // A time already in the clinic's own window is not an extra hour; keeping the
  // duplicate would render the slot twice and let it be booked past capacity.
  const regularSet = new Set(regular);
  const extra = [...new Set((extraSlots || []).map(String))].filter((t) => !regularSet.has(t));

  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const isTodayDate = dateKey === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  const decorate = (time, isExtra) => {
    const taken = countBookingsInSlot(bookings, { clinicId: clinic?.id, dateKey, time });
    const isPast = isTodayDate && timeToMinutes(time) <= nowMinutes;
    return {
      time,
      taken,
      capacity,
      available: taken < capacity && !isPast,
      isPast,
      isExtra,
    };
  };

  return [
    ...regular.map((time) => decorate(time, false)),
    ...extra.sort().map((time) => decorate(time, true)),
  ];
}

/**
 * First free slot at or after `fromTime` on `dateKey`.
 * Replaces the legacy `findNextAvailableSlot`, which searched the DOM.
 *
 * @returns {string|null}
 */
export function findNextAvailableSlot(clinic, dateKey, bookings, fromTime = null) {
  const floor = fromTime ? timeToMinutes(fromTime) : -1;
  const slots = clinicSlotsForDate(clinic, dateKey);
  for (const time of slots) {
    if (timeToMinutes(time) <= floor) continue;
    if (isSlotAvailable(bookings, clinic, dateKey, time)) return time;
  }
  return null;
}

/** True when the clinic is open on that weekday. */
export function isClinicWorkingDay(clinic, dateKey) {
  const workingDays = Array.isArray(clinic?.workingDays) ? clinic.workingDays : [];
  if (workingDays.length === 0) return true; // no restriction configured
  return workingDays.some((d) => String(d).toLowerCase() === dayKeyOf(dateKey));
}
